import React, { useState, useEffect, useRef, useCallback } from 'react';

const AppSection = () => {
    const [scrollProgress, setScrollProgress] = useState(0);
    const [videosLoaded, setVideosLoaded] = useState({});
    const [videoErrors, setVideoErrors] = useState({});
    const sectionRef = useRef(null);
    const videoRefs = useRef([]);

    const contentData = [
        {
            heading: "Book a Load Instantly",
            subheading: "Enter pickup location, destination, cargo details, and book your load in seconds! Our streamlined booking process eliminates paperwork and gets your shipment moving fast.",
            description: "Experience the future of freight booking with our intelligent platform. Simply input your cargo specifications, preferred pickup times, and destination details. Our system instantly matches you with verified carriers, provides transparent pricing, and confirms your booking within moments. No more endless phone calls or waiting for quotes - just efficient, reliable freight solutions at your fingertips.",
            features: ["One-click booking system", "Instant price quotes", "Verified carrier network", "Digital documentation"],
            highlight: "Instant Booking!",
            videoSrc: "https://prod-roadexpress.s3.ap-south-1.amazonaws.com/website/shipperanimation1.mp4",
            fallbackIcon: "📦"
        },
        {
            heading: "Real-Time Order Tracking",
            subheading: "Monitor all order details with live updates and negotiate directly with RoadExpress representatives. Complete transparency from booking to delivery with instant notifications.",
            description: "Stay connected to your shipments like never before. Our advanced tracking system provides minute-by-minute updates, allowing you to monitor pickup confirmations, transit progress, and delivery status. Communicate directly with our logistics team through integrated chat, negotiate terms in real-time, and receive automated alerts for any changes or delays.",
            features: ["Live shipment tracking", "Direct communication portal", "Automated notifications", "Real-time negotiations"],
            highlight: "Live Updates!",
            videoSrc: "https://prod-roadexpress.s3.ap-south-1.amazonaws.com/website/shipperanimation2.mp4",
            fallbackIcon: "📋"
        },
        {
            heading: "Live Trip Monitoring",
            subheading: "Track all trips with real-time status updates, vehicle changes, and driver notifications. Stay informed about every aspect of your shipment's journey.",
            description: "Transform your logistics oversight with comprehensive trip monitoring capabilities. Watch as your cargo moves across the country with GPS-enabled tracking, receive instant notifications about driver changes, vehicle maintenance stops, or route modifications. Our platform keeps you informed about weather conditions, traffic delays, and estimated arrival times, ensuring you're always one step ahead.",
            features: ["GPS vehicle tracking", "Driver communication", "Route optimization", "Weather & traffic alerts"],
            highlight: "Real-Time Trips!",
            videoSrc: "https://prod-roadexpress.s3.ap-south-1.amazonaws.com/website/shipperanimation3.mp4",
            fallbackIcon: "🚛"
        },
        {
            heading: "Complete Trip Management",
            subheading: "Access comprehensive trip details including real-time status, payment updates, direct driver communication, and live driver location tracking for complete peace of mind.",
            description: "Take control of your logistics operations with our all-in-one trip management dashboard. Monitor payment statuses, access detailed trip histories, communicate directly with drivers through secure messaging, and track vehicle locations in real-time. Generate comprehensive reports, manage multiple shipments simultaneously, and maintain complete visibility over your freight operations from a single, intuitive interface.",
            features: ["Centralized dashboard", "Payment tracking", "Driver messaging", "Comprehensive reporting"],
            highlight: "Full Control!",
            videoSrc: "https://prod-roadexpress.s3.ap-south-1.amazonaws.com/website/shipperanimation4.mp4",
            fallbackIcon: "📊"
        },
        {
            heading: "AI-Powered Route Planning",
            subheading: "Get real-time vehicle location with AI-based traffic analysis, optimal route suggestions, and weather updates for the most efficient and safe transportation solutions.",
            description: "Harness the power of artificial intelligence for superior logistics planning. Our AI algorithms analyze historical traffic patterns, current road conditions, weather forecasts, and fuel efficiency data to recommend the most cost-effective and time-efficient routes. Reduce transportation costs, minimize delivery times, and ensure safer journeys with our intelligent routing technology that adapts to changing conditions in real-time.",
            features: ["AI route optimization", "Traffic pattern analysis", "Weather integration", "Fuel efficiency tracking"],
            highlight: "Smart Routes!",
            videoSrc: "https://prod-roadexpress.s3.ap-south-1.amazonaws.com/website/shipperanimation5.mp4",
            fallbackIcon: "🗺️"
        }
    ];

    // Calculate current video index and transition progress for smooth fading
    const getVideoTransition = useCallback(() => {
        const totalSections = contentData.length;
        const sectionProgress = scrollProgress * totalSections;
        const currentVideoIndex = Math.min(Math.floor(sectionProgress), totalSections - 1);
        const nextVideoIndex = Math.min(currentVideoIndex + 1, totalSections - 1);

        // Calculate fade progress within current section
        const withinSectionProgress = sectionProgress - currentVideoIndex;
        const fadeThreshold = 0.8; // Start fading at 80% through section

        let fadeProgress = 0;
        if (withinSectionProgress > fadeThreshold && currentVideoIndex < totalSections - 1) {
            fadeProgress = (withinSectionProgress - fadeThreshold) / (1 - fadeThreshold);
        }

        return {
            currentIndex: currentVideoIndex,
            nextIndex: nextVideoIndex,
            fadeProgress: Math.max(0, Math.min(1, fadeProgress))
        };
    }, [scrollProgress, contentData.length]);

    const { currentIndex, nextIndex, fadeProgress } = getVideoTransition();

    // Enhanced smooth scroll handling with continuous progress tracking
    useEffect(() => {
        let ticking = false;
        let lastScrollTime = 0;

        const handleScroll = () => {
            const now = Date.now();

            if (!ticking && now - lastScrollTime > 8) { // ~120fps throttling for smoother response
                requestAnimationFrame(() => {
                    if (!sectionRef.current) {
                        ticking = false;
                        return;
                    }

                    const sectionRect = sectionRef.current.getBoundingClientRect();
                    const sectionHeight = sectionRect.height;
                    const windowHeight = window.innerHeight;

                    // Calculate scroll progress with better boundaries
                    const startPoint = sectionRect.top;
                    const endPoint = sectionRect.bottom - windowHeight;
                    const totalScrollDistance = sectionHeight - windowHeight;

                    let progress = 0;
                    if (startPoint > 0) {
                        // Before the section starts
                        progress = 0;
                    } else if (endPoint < 0) {
                        // After the section ends
                        progress = 1;
                    } else {
                        // Within the section
                        progress = Math.abs(startPoint) / totalScrollDistance;
                        progress = Math.max(0, Math.min(1, progress));
                    }

                    // Update scroll progress for smooth text movement
                    setScrollProgress(progress);

                    ticking = false;
                    lastScrollTime = now;
                });
                ticking = true;
            }
        };

        // Use passive scrolling for better performance
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Handle video playback based on current index with smooth transitions
    useEffect(() => {
        videoRefs.current.forEach((video, index) => {
            if (video) {
                try {
                    // Play current video and next video during transition
                    if (index === currentIndex || (index === nextIndex && fadeProgress > 0)) {
                        if (video.paused) {
                            video.currentTime = 0;
                            video.play().catch(() => { });
                        }
                    } else {
                        if (!video.paused) {
                            video.pause();
                        }
                    }
                } catch (error) {
                    console.warn(`Video control error for index ${index}:`, error);
                }
            }
        });
    }, [currentIndex, nextIndex, fadeProgress]);

    const handleVideoLoad = useCallback((index) => {
        setVideosLoaded(prev => ({ ...prev, [index]: true }));

        // Auto-play first video when it loads
        if (index === 0 && videoRefs.current[0]) {
            videoRefs.current[0].play().catch(() => { });
        }
    }, []);

    const handleVideoError = useCallback((index) => {
        setVideoErrors(prev => ({ ...prev, [index]: true }));
        console.error(`Failed to load video ${index + 1}`);
    }, []);

    // Calculate video opacity for smooth transitions
    const getVideoOpacity = (index) => {
        if (index === currentIndex) {
            return fadeProgress > 0 ? 1 - fadeProgress : 1;
        } else if (index === nextIndex && fadeProgress > 0) {
            return fadeProgress;
        }
        return 0;
    };

    // Preload videos for smoother transitions
    useEffect(() => {
        contentData.forEach((content, index) => {
            if (videoRefs.current[index] && !videosLoaded[index] && !videoErrors[index]) {
                videoRefs.current[index].load();
            }
        });
    }, [videosLoaded, videoErrors, contentData]);

    // Calculate smooth text transform based on scroll progress
    const getTextTransform = () => {
        // Calculate the position for the sliding text container
        const totalSections = contentData.length;
        const sectionProgress = scrollProgress * (totalSections - 1);
        const translateY = sectionProgress * 100; // Use viewport height units
        return `translateY(-${translateY}vh)`;
    };

    return (
        <div className="relative w-full">
            {/* Extended scroll section - height matches number of sections */}
            <section
                ref={sectionRef}
                className="min-h-[500vh] bg-gray-50 relative w-full"
                aria-labelledby="mobile-app-section-title"
            >
                {/* Container positioned relative to scroll normally */}
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="sticky top-0 h-screen flex flex-col justify-center z-10 w-full">

                        {/* Main Content Grid */}
                        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center px-4 sm:px-6 lg:px-8 w-full">

                            {/* Video Section (Left) - Full width, no constraints */}
                            <div className="relative w-full">
                                {/* Video container for landscape videos - adjusted aspect ratio */}
                                <div className="relative overflow-hidden rounded-2xl aspect-[8/10] max-w-2xl mx-auto w-full">
                                    {/* Video stack */}
                                    {contentData.map((content, index) => {
                                        const opacity = getVideoOpacity(index);

                                        return (
                                            <div
                                                key={index}
                                                className="absolute inset-0 h-full w-full"
                                                style={{
                                                    opacity: opacity,
                                                    zIndex: index === currentIndex || index === nextIndex ? 10 : 1,
                                                    transition: 'opacity 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)'
                                                }}
                                            >
                                                {!videoErrors[index] ? (
                                                    <video
                                                        ref={el => {
                                                            if (el) videoRefs.current[index] = el;
                                                        }}
                                                        src={content.videoSrc}
                                                        className="w-full h-full object-cover"
                                                        onLoadedData={() => handleVideoLoad(index)}
                                                        onError={() => handleVideoError(index)}
                                                        loop
                                                        muted
                                                        playsInline
                                                        preload="metadata"
                                                        controls={false}
                                                        aria-label={`${content.heading} demonstration video`}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-orange-100">
                                                        <div className="text-center p-4">
                                                            <div className="text-4xl mb-2">{content.fallbackIcon}</div>
                                                            <div className="text-lg font-semibold text-gray-800">{content.heading}</div>
                                                            <div className="text-sm text-gray-600">Feature Preview</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Text Content Section (Right) */}
                            <div className="relative h-screen overflow-hidden w-full">
                                {/* Sliding container with smooth continuous movement */}
                                <div
                                    className="will-change-transform"
                                    style={{
                                        transform: getTextTransform(),
                                        transition: 'none' // Remove transition for smooth scroll-based movement
                                    }}
                                >
                                    {/* Text content sections */}
                                    {contentData.map((content, index) => (
                                        <div
                                            key={index}
                                            className="h-screen flex flex-col justify-center space-y-6 px-4"
                                        >
                                            {/* Badge */}
                                            <div className="mb-4">
                                                <span className="inline-block bg-[#F05A28]/10 text-[#F05A28] px-4 py-2 rounded-full text-sm font-semibold">
                                                    {content.highlight}
                                                </span>
                                            </div>

                                            {/* Heading */}
                                            <div className="mb-6">
                                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 leading-tight">
                                                    {content.heading.split(' ').map((word, wordIndex) => (
                                                        <span
                                                            key={`${index}-${wordIndex}`}
                                                            className={`inline-block mr-2 ${['Instantly', 'Tracking', 'Monitoring', 'Management', 'Planning'].some(highlight =>
                                                                word.includes(highlight)
                                                            ) ? 'text-[#F05A28]' : ''
                                                                }`}
                                                        >
                                                            {word}
                                                        </span>
                                                    ))}
                                                </h2>
                                            </div>

                                            {/* Extended Description */}
                                            <div className="mb-8">
                                                <p className="text-base text-gray-700 leading-relaxed">
                                                    {content.description}
                                                </p>
                                            </div>

                                            {/* Feature List */}
                                            <div className="mb-6">
                                                <div className="grid grid-cols-2 gap-3">
                                                    {content.features.map((feature, featureIndex) => (
                                                        <div key={featureIndex} className="flex items-center space-x-2">
                                                            <div className="w-2 h-2 bg-[#F05A28] rounded-full flex-shrink-0"></div>
                                                            <span className="text-sm text-gray-600 font-medium">{feature}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SEO structured data */}
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "MobileApplication",
                            "name": "RoadExpress Logistics Platform",
                            "description": "Professional logistics management platform with real-time tracking, AI-powered route optimization, and comprehensive freight management solutions",
                            "applicationCategory": "BusinessApplication",
                            "operatingSystem": "Web, iOS, Android",
                            "offers": {
                                "@type": "Offer",
                                "price": "0",
                                "priceCurrency": "INR"
                            },
                            "author": {
                                "@type": "Organization",
                                "name": "RoadExpress"
                            }
                        })
                    }}
                />
            </section>
        </div>
    );
};

export default AppSection;